import { getIn } from '@shared/util/object';
import { DEV } from 'esm-env';
import { createStore, StoreApi } from 'zustand';
import Yup from '../yupWithCustomValidations';
import { SubmitFunction } from './Form';
import { cleanUpErrors, parseYupErrors, updateIn } from './utils';

export type ValidationMode = 'onBlur' | 'onChange' | 'onSubmit';
export type FormProps<T> = {
  initialValues: T;
  validationSchema: Yup.AnyObjectSchema;
  validationMode?: ValidationMode;
  onSubmit: SubmitFunction<T>;
};

type BasicValue = string | boolean | number | Date | File | FileList;

export type Value = BasicValue | Value[] | { [key: string]: Value } | null | undefined;

export type InitialValues = Record<string, Value>;

export type FormTouched<T> = {
  [K in keyof T]?: T[K] extends Value ? boolean : boolean[];
};

export type FormErrors<T> = {
  [K in keyof T]?: T[K] extends Value ? string : Array<string | null>;
};

export type ValidatedHandler<T> = (errors: FormErrors<T>) => void;

export type Actions<T = InitialValues> = {
  deleteFromArrayField: (name: string, index: number) => void;
  pushToArrayField: (name: string, value: Value) => void;
  setFieldValue: (pathOrName: string, value: Value) => void;
  setFieldError: (pathOrName: string, error: string) => void;
  setFieldTouched: (pathOrName: string) => void;
  resetForm: (values?: T) => void;
  resetErrorState: () => void;
  validate: (field?: string) => Promise<boolean>;
  submitForm: () => void;
};

export type Store<T = InitialValues> = {
  values: T;
  touched: FormTouched<T>;
  errors: FormErrors<T>;
  initialValues: T;
  isValidating: boolean;
  isSubmitting: boolean;
  dirty: boolean;
  onValidated?: ValidatedHandler<T>;
  actions: Actions<T>;
  validationSchema: Yup.AnyObjectSchema;
  onSubmit: SubmitFunction<T>;
};

export const validateForm = <T>({ validationSchema, store, field }: ValidateForm<T>) =>
  validationSchema ? store.getState().actions.validate(field) : Promise.resolve(true);

type CreateFormStoreParams<T> = {
  initialValues: T;
  onValidated?: ValidatedHandler<T>;
  validationSchema: Yup.AnyObjectSchema;
  onSubmit: SubmitFunction<T>;
};
export const createFormStore = <T extends InitialValues>({
  initialValues,
  ...rest
}: CreateFormStoreParams<T>) => {
  const rawState = {
    values: initialValues,
    initialValues,
    touched: {},
    errors: {},
    isValidating: false,
    isSubmitting: false,
    dirty: false,
    ...rest,
  };

  return createStore<Store<T>>((set, get) => ({
    ...rawState,
    actions: {
      setFieldValue: (pathOrName, value) =>
        set(({ values }) => ({
          values: updateIn(values, pathOrName, value),
          dirty: true,
        })),
      submitForm: async () => {
        try {
          set({ isSubmitting: true });
          const { actions, onSubmit, values } = get();
          const isValid = await actions.validate();
          if (isValid) {
            return await onSubmit(values, {
              ...actions,
              validateForm: () => actions.validate(),
            });
          }
        } finally {
          set({ isSubmitting: false });
        }
      },
      setFieldTouched: (pathOrName) =>
        set(({ touched }) => ({
          touched: updateIn(touched, pathOrName, true),
        })),
      resetForm: (values) =>
        set(() => ({
          ...rawState,
          ...(values ? { values } : {}),
        })),
      resetErrorState: () => set(() => ({ errors: {}, touched: {}, dirty: false })),
      pushToArrayField: (name, value) => {
        set(({ values }) => {
          const fieldValues = getIn<Value>(values, name);
          if (fieldValues && Array.isArray(fieldValues)) {
            return {
              values: updateIn(values, name, [...fieldValues, value]),
            };
          }
          return {};
        });
      },
      setFieldError: (pathOrName, error) =>
        set(({ errors }) => ({
          errors: updateIn(errors, pathOrName, error),
          dirty: true,
        })),
      deleteFromArrayField: (name, index) => {
        set(({ values, errors, touched }) => {
          const currentValues = getIn<Value[]>(values, name);
          const currentErrors = getIn<Value[]>(errors, name);
          const currentTouched = getIn<Value[]>(touched, name);
          return {
            values: Array.isArray(currentValues)
              ? updateIn(
                  values,
                  name,
                  [...currentValues].filter((_, i) => i !== index),
                )
              : undefined,
            errors: Array.isArray(currentErrors)
              ? updateIn(
                  errors,
                  name,
                  [...currentErrors].filter((_, i) => i !== index),
                )
              : undefined,
            touched: Array.isArray(currentTouched)
              ? updateIn(
                  touched,
                  name,
                  [...currentTouched].filter((_, i) => i !== index),
                )
              : undefined,
          };
        });
      },
      validate: async (field?: string): Promise<boolean> => {
        const { validationSchema } = get();
        set({ isValidating: true });
        try {
          // When the optional param "field" is passed "validateAt" is used
          // to validate one single field instead of all fields.
          const params = [get().values, { abortEarly: false }] as const;
          const validationCall = field
            ? validationSchema.validateAt(field, ...params)
            : validationSchema.validate(...params);

          await validationCall;
          set(({ errors }) => {
            let errorsData = field ? { ...errors } : {};

            if (field) {
              errorsData = cleanUpErrors(updateIn(errorsData, field, null));
            }

            return {
              errors: errorsData,
              isValidating: false,
            };
          });
          return true;
        } catch (err) {
          if (err instanceof Yup.ValidationError) {
            const yupErrors = parseYupErrors(err);
            // it's hard to debug validation errors for unbound fields so let's leave this in dev mode
            if (DEV) {
              // eslint-disable-next-line no-console
              console.log('form validation failed:', yupErrors);
            }
            set(({ errors }) => {
              let newErrorsState = field ? errors : {};

              Object.entries(yupErrors).forEach(([path, value]) => {
                newErrorsState = updateIn(newErrorsState, path, value);
              });

              return {
                errors: newErrorsState,
                isValidating: false,
              };
            });
            return false;
          }

          // This means that we simply don't have validation for the field
          if (
            err instanceof Error &&
            err.message.includes('The schema does not contain the path:')
          ) {
            if (DEV) {
              console.error(err.message);
            }
            return true;
          }

          throw err;
        } finally {
          get().onValidated?.(get().errors);
        }
      },
    },
  }));
};

type ValidateForm<T> = {
  validationSchema?: Yup.AnyObjectSchema;
  store: StoreApi<Store<T>>;
  field?: string;
};
